import { useState, useEffect } from 'react';
import useTripStore from '../../context/TripStore';
import RouteOptionsCard from './RouteOptionsCard';

/**
 * Mobile navigation view - Google Maps style
 * Map takes most of screen, bottom sheet with directions
 */
function MobileNavigationView() {
  const trip = useTripStore((state) => state.trip);
  const places = useTripStore((state) => state.places ?? []);
  const itinerary = useTripStore((state) => state.itinerary);
  const navigation = useTripStore((state) => state.navigation);
  const settings = useTripStore((state) => state.settings);
  const stopNavigation = useTripStore((state) => state.stopNavigation);
  const goToNextStop = useTripStore((state) => state.goToNextStop);
  const completeStop = useTripStore((state) => state.completeStop);
  const setCurrentStopIndex = useTripStore((state) => state.setCurrentStopIndex);
  const setRouteAlternatives = useTripStore((state) => state.setRouteAlternatives);
  const selectRoute = useTripStore((state) => state.selectRoute);

  const [isExpanded, setIsExpanded] = useState(false);
  const [directions, setDirections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const activePlan = itinerary?.activePlan === 'a' ? itinerary?.planA : itinerary?.planB;
  const { currentStopIndex: stopIndex, completedStops, routeAlternatives, selectedRouteIndex } = navigation || {};
  const currentStop = activePlan?.stops?.[stopIndex];
  const nextStop = activePlan?.stops?.[stopIndex + 1];
  const isLastStop = stopIndex === (activePlan?.stops?.length || 0) - 1;
  const totalStops = activePlan?.stops?.length || 0;
  const progress = totalStops > 0 ? ((stopIndex + 1) / totalStops) * 100 : 0;
  const travelMode = settings?.travelMode || 'WALKING';

  // Center map on current stop
  useEffect(() => {
    if (currentStop && window.backgroundMapInstance) {
      const place = places.find(p => p.name === currentStop.name || p.placeId === currentStop.placeId);
      if (place?.coordinates) {
        window.backgroundMapInstance.setCenter(place.coordinates);
        window.backgroundMapInstance.setZoom(16);
      }
    }
  }, [stopIndex, currentStop, places]);

  // Fetch directions with alternatives
  useEffect(() => {
    if (!currentStop) return;

    let origin = null;
    let destination = null;

    if (stopIndex === 0) {
      origin = trip?.startLocation?.coordinates;
      const currentPlace = places.find(p => p.name === currentStop.name || p.placeId === currentStop.placeId);
      destination = currentPlace?.coordinates;
    } else {
      const previousStop = activePlan?.stops?.[stopIndex - 1];
      const previousPlace = places.find(p => p.name === previousStop?.name || p.placeId === previousStop?.placeId);
      const currentPlace = places.find(p => p.name === currentStop.name || p.placeId === currentStop.placeId);
      origin = previousPlace?.coordinates;
      destination = currentPlace?.coordinates;
    }

    if (!origin || !destination || !window.google?.maps?.DirectionsService) {
      setDirections(null);
      return;
    }

    setLoading(true);

    const directionsService = new window.google.maps.DirectionsService();

    const request = {
      origin,
      destination,
      travelMode: window.google.maps.TravelMode[travelMode],
      provideRouteAlternatives: travelMode !== 'TRANSIT', // Transit doesn't support alternatives well
    };

    if (travelMode === 'WALKING' || travelMode === 'BICYCLING') {
      request.avoidHighways = true;
      request.avoidFerries = true;
    } else if (travelMode === 'TRANSIT') {
      request.transitOptions = {
        departureTime: new Date(),
        modes: ['BUS', 'SUBWAY', 'TRAIN', 'TRAM', 'RAIL'],
        routingPreference: 'LESS_WALKING',
      };
    }

    directionsService.route(request, (result, status) => {
      setLoading(false);
      if (status === 'OK' && result.routes?.length > 0) {
        // Process all routes
        const routes = result.routes.map((route, idx) => {
          const leg = route.legs[0];
          return {
            index: idx,
            summary: route.summary || `Route ${idx + 1}`,
            duration: leg.duration,
            distance: leg.distance,
            steps: leg.steps.map((step, stepIdx) => ({
              index: stepIdx + 1,
              instruction: step.instructions.replace(/<[^>]*>/g, ''),
              distance: step.distance.text,
              duration: step.duration.text,
              travelMode: step.travel_mode,
              transit: step.travel_mode === 'TRANSIT' && step.transit ? {
                name: step.transit.line?.short_name || step.transit.line?.name || 'Transit',
                vehicle: step.transit.line?.vehicle || { type: 'TRANSIT' },
                departureStop: step.transit.departure_stop?.name || 'Stop',
                arrivalStop: step.transit.arrival_stop?.name || 'Stop',
                numStops: step.transit.num_stops || 0,
                headsign: step.transit.headsign || '',
              } : null,
            })),
          };
        });

        // Store alternatives in global state for map rendering
        if (setRouteAlternatives) {
          setRouteAlternatives(routes);
        }

        // Use selected route or first route
        const activeRoute = routes[selectedRouteIndex || 0] || routes[0];
        setDirections(activeRoute);
        setCurrentStepIndex(0);
      } else {
        setDirections(null);
        if (setRouteAlternatives) {
          setRouteAlternatives([]);
        }
      }
    });
  }, [stopIndex, currentStop, activePlan, places, trip, travelMode, setRouteAlternatives, selectedRouteIndex]);

  // Update directions when route selection changes
  useEffect(() => {
    if (routeAlternatives?.length > 0 && selectedRouteIndex !== undefined) {
      const activeRoute = routeAlternatives[selectedRouteIndex];
      if (activeRoute) {
        setDirections(activeRoute);
        setCurrentStepIndex(0);
      }
    }
  }, [selectedRouteIndex, routeAlternatives]);

  const handleSelectRoute = (index) => {
    if (selectRoute) {
      selectRoute(index);
    }
  };

  const handleEndNavigation = () => {
    stopNavigation();
  };

  const currentStep = directions?.steps?.[currentStepIndex];
  const sheetHeight = isExpanded ? '70vh' : '40vh';

  return (
    <>
      {/* Map visible behind - most of screen */}

      {/* Top bar with progress and end button */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-900">
                Stop {stopIndex + 1} of {totalStops}
              </span>
              <span className="text-xs text-gray-500">
                {directions?.duration?.text || '...'}
              </span>
            </div>
            <button
              onClick={handleEndNavigation}
              className="text-sm font-medium text-red-600 hover:text-red-700"
            >
              End
            </button>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-blue-600 h-1 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Route alternatives - floating above bottom sheet */}
      {routeAlternatives?.length > 1 && (
        <div className="fixed z-40 left-0 right-0" style={{ bottom: sheetHeight }}>
          <RouteOptionsCard
            routes={routeAlternatives}
            selectedIndex={selectedRouteIndex || 0}
            onSelectRoute={handleSelectRoute}
          />
        </div>
      )}

      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-3xl shadow-2xl transition-all duration-300 ease-out flex flex-col"
        style={{ height: sheetHeight, maxHeight: sheetHeight }}
      >
        {/* Drag handle */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex justify-center pt-3 pb-2 w-full"
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </button>

        {/* Current stop header */}
        <div className="px-4 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 mb-0.5">Heading to</p>
              <h2 className="text-lg font-semibold text-gray-900 truncate">{currentStop?.name}</h2>
              <p className="text-sm text-gray-500">
                Arrive {currentStop?.arrival_time} · {currentStop?.duration_minutes} min visit
              </p>
            </div>
            <div className="flex-shrink-0 ml-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                {stopIndex + 1}
              </div>
            </div>
          </div>
        </div>

        {/* Current direction */}
        {loading ? (
          <div className="px-4 py-6 text-center text-gray-500 text-sm">
            Loading directions...
          </div>
        ) : currentStep ? (
          <div className="px-4 py-3 bg-gray-900 text-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                {currentStep.travelMode === 'TRANSIT' ? '🚇' : currentStep.travelMode === 'WALKING' ? '🚶' : '➡️'}
              </div>
              <div className="flex-1 min-w-0">
                {currentStep.transit && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-blue-600 rounded text-xs font-medium">
                      {currentStep.transit.name}
                    </span>
                    {currentStep.transit.numStops > 0 && (
                      <span className="text-xs text-gray-300">
                        {currentStep.transit.numStops} stops
                      </span>
                    )}
                  </div>
                )}
                <p className="text-sm font-medium">{currentStep.instruction}</p>
                <p className="text-xs text-gray-300 mt-1">
                  {currentStep.distance} · {currentStep.duration}
                </p>
              </div>
            </div>

            {/* Step navigation */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                disabled={currentStepIndex === 0}
                className="flex-1 py-2 text-sm bg-white/20 rounded-lg disabled:opacity-30"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentStepIndex(Math.min((directions?.steps?.length || 1) - 1, currentStepIndex + 1))}
                disabled={currentStepIndex === (directions?.steps?.length || 1) - 1}
                className="flex-1 py-2 text-sm bg-white/20 rounded-lg disabled:opacity-30"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {/* All steps */}
          {directions?.steps?.length > 0 && (
            <div className="space-y-2 mb-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                All Steps ({directions.steps.length})
              </p>
              {directions.steps.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStepIndex(idx)}
                  className={`w-full text-left flex items-start gap-3 p-2 rounded-lg transition-colors ${
                    idx === currentStepIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                    idx === currentStepIndex
                      ? 'bg-blue-600 text-white'
                      : step.travelMode === 'TRANSIT'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                  }`}>
                    {step.travelMode === 'TRANSIT' ? '🚇' : step.travelMode === 'WALKING' ? '🚶' : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${idx === currentStepIndex ? 'font-medium text-blue-900' : 'text-gray-700'}`}>
                      {step.instruction}
                    </p>
                    <p className="text-xs text-gray-500">{step.distance} · {step.duration}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Next stop preview */}
          {nextStop && (
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Next Stop</p>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                    {stopIndex + 2}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{nextStop.name}</p>
                    <p className="text-xs text-gray-500">Arrive {nextStop.arrival_time}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Remaining stops mini overview */}
          {activePlan?.stops?.length > stopIndex + 2 && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                {activePlan.stops.length - stopIndex - 1} stops remaining
              </p>
              <div className="flex gap-1 flex-wrap">
                {activePlan.stops.slice(stopIndex + 1).map((stop, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentStopIndex(stopIndex + 1 + idx)}
                    className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs text-gray-600 hover:bg-gray-200"
                  >
                    {stopIndex + 2 + idx}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Extra space for action buttons */}
          <div className="h-24" />
        </div>

        {/* Fixed action buttons */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 flex gap-3" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
          <button
            onClick={() => completeStop(stopIndex)}
            disabled={completedStops?.includes(stopIndex)}
            className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
              completedStops?.includes(stopIndex)
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700 active:bg-gray-200'
            }`}
          >
            {completedStops?.includes(stopIndex) ? '✓ Done' : 'Mark Done'}
          </button>
          {!isLastStop && (
            <button
              onClick={goToNextStop}
              className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl active:bg-blue-700"
            >
              Next Stop →
            </button>
          )}
          {isLastStop && (
            <button
              onClick={handleEndNavigation}
              className="flex-1 py-3 bg-green-600 text-white font-medium rounded-xl active:bg-green-700"
            >
              Finish Trip
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default MobileNavigationView;
